// Generated by strategy/render/build.py — do not edit.
// Source of truth: strategy/guide/*.md
export const GUIDE_CHAPTERS = [
 {
  "slug": "01-the-board",
  "title": "The board",
  "blocks": [
   {
    "type": "md",
    "html": "<h1>The board</h1>\n<p>Twenty-three stopping points joined by forty-two pathways. Every line is the\nsame length, so each point sits exactly one step from each of its neighbours,\nand a move is always a step to an adjacent point.</p>"
   },
   {
    "type": "figure",
    "caption": "The board, with every point named.",
    "board": {
     "checkers": {
      "C1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C2": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C7": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C8": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "D1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "D2": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "D3": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "D4": {
       "color": "BLACK",
       "isUpgraded": false
      }
     },
     "arrows": [],
     "highlightVertices": [],
     "sub": null
    }
   },
   {
    "type": "md",
    "html": "<p>The points fall into four groups, and the names in this guide follow them.</p>\n<p><strong>A1</strong> is the hub. It touches all six points of the inner hexagon and nothing\nelse. Six connections is the highest count on the board, and it costs more than\nit pays. The chapter on board control comes back to it.</p>\n<p><strong>B1 to B6</strong> form the inner hexagon. Each one touches the hub, two neighbours\naround the hexagon, and two points on the outer ring: five connections each.</p>\n<p><strong>C1 to C12</strong> form the outer ring, a dodecagon. Connections here vary between\nthree and four.</p>\n<p><strong>D1 to D4</strong> are the four home points, two at each end of the board. They are\nthe quietest squares on it, with two connections apiece.</p>\n<h2>The two home bases</h2>\n<p>White owns C1, C2, D1 and D2. Black owns C7, C8, D3 and D4. Each base is a\nsquare of four points closed by four pathways.</p>\n<p>A home base does three separate jobs, and it is worth keeping all three in mind\nbecause they pull in different directions:</p>\n<ol>\n<li>A cob standing on its own base may move in any direction, not only forward.\n   Your own base is the one place a cob can retreat from.</li>\n<li>A cob that lands on the opponent's base is promoted to a rok at once.</li>\n<li>A cob captured while standing on its own base stays a cob. It changes owner\n   and keeps its rank.</li>\n</ol>\n<h2>Writing a move</h2>\n<p>A move is written as the point it leaves and the point it reaches, joined by a\nhyphen. <code>C1-B1</code> moves whatever stands on C1 to B1. Captures are not marked,\nbecause a capture is a consequence of the move rather than a choice.</p>\n<p>One move looks odd at first: <code>D3-D3</code>, a point moving to itself. That is the\nforced promotion of a dead cob, and the endgame chapter explains when it\nhappens.</p>\n<h2>The board is symmetric twice over</h2>\n<p>Turn the board through half a circle and White's half lands exactly on Black's.\nUnder that rotation A1 maps to itself, each B point maps to the one opposite,\neach C point to the one six around, and the two home bases swap. Tarati is\ntherefore perfectly balanced between the colours, and the only real difference\nbetween the two players is that White moves first.</p>\n<p>That single fact is worth holding onto. Any time one colour appears to be\nwinning far more often than the other, something other than the game is\nproducing it.</p>\n<p>There is a second symmetry, and it is the more useful one at the board. Fold\nthe board along the line running through B1, A1 and B4. The left half lands on\nthe right half, and the colours stay where they are. The opening position is\nunchanged by this fold.</p>"
   },
   {
    "type": "figure",
    "caption": "The fold axis runs through B1, A1 and B4. The two halves are mirror images.",
    "board": {
     "checkers": {
      "C1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C2": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C7": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C8": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "D1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "D2": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "D3": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "D4": {
       "color": "BLACK",
       "isUpgraded": false
      }
     },
     "arrows": [],
     "highlightVertices": [
      "B1",
      "A1",
      "B4"
     ],
     "sub": null
    }
   },
   {
    "type": "md",
    "html": "<p>So the four moves White can legally make on move one are two moves seen\ntwice:</p>\n<table>\n<thead>\n<tr>\n<th>Move</th>\n<th>Mirror image</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td>C1-C12</td>\n<td>C2-C3</td>\n</tr>\n<tr>\n<td>C1-B1</td>\n<td>C2-B1</td>\n</tr>\n</tbody>\n</table>\n<p><code>C1-C12</code> steps along the outer ring. <code>C1-B1</code> steps inward toward the hub. Every\nopening in Tarati begins with one of those two ideas, and each can be played\nfrom either side of the board. Half the opening theory you might expect\ndoes not exist.</p>"
   }
  ]
 },
 {
  "slug": "03-movement",
  "title": "How the pieces move",
  "blocks": [
   {
    "type": "md",
    "html": "<h1>How the pieces move</h1>\n<p>Every piece starts as a cob. Some become roks. The difference between them is\nthe difference between a piece that can only go forward and one that can go\nanywhere, and almost every plan in Tarati turns on it.</p>\n<h2>Cobs go forward</h2>\n<p>A cob moves one step to an adjacent point, and only toward the enemy. It can\nnever step sideways along a rank, and it can never retreat. Whatever a cob has\nwalked past is behind it for good.</p>\n<p>This gives a cob a strange, committing quality. A cob is a promise you cannot\ntake back. Push it up the board and it gains ground and loses options at the\nsame rate, until it either promotes on the far home base or runs out of squares.</p>\n<p>There is one exception, and it is deliberate. A cob standing on one of its own\nhome points may move in any direction, forward or back or across. Home is the\none place a cob is not yet committed. In practice this means your back rank can\nanswer a threat that a cob out in the field never could, so there is a real cost\nto emptying your own base too early.</p>\n<h2>Roks go anywhere</h2>\n<p>A cob that reaches the opponent's home base is promoted to a rok, and a rok\nmoves one step in any direction. Forward, backward, sideways, all the same.</p>\n<p>That freedom is worth more than the extra material. A rok can retreat to defend\na point under threat, cross the board to attack from a side the enemy has left\nopen, and, above all, leave a square and come back to it. The striking chapter\nshowed why coming back matters: you can only capture an enemy by arriving from a\nline you did not already command, and a cob, which never turns around, can\nalmost never arrange that against a piece it is facing. A rok can. This is why\nattacks in the corpus are so often led by a promoted piece.</p>\n<h2>Dead cobs</h2>\n<p>Because a cob can only go forward, a cob with no forward left is stuck forever.</p>"
   },
   {
    "type": "figure",
    "caption": "The black cob on D2 is dead. It sits at the far edge with no square ahead of it, and it will never move again.",
    "board": {
     "checkers": {
      "B1": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "B4": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C2": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C7": {
       "color": "WHITE",
       "isUpgraded": true
      },
      "D1": {
       "color": "BLACK",
       "isUpgraded": true
      },
      "D2": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "D3": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "D4": {
       "color": "WHITE",
       "isUpgraded": false
      }
     },
     "arrows": [],
     "highlightVertices": [
      "D2"
     ],
     "sub": null
    }
   },
   {
    "type": "md",
    "html": "<p>That black cob is on D2, deep inside white's home, at the very end of the board.\nA black cob advances toward this edge, so from here there is no \"forward\" at\nall. Both its neighbours are occupied as well, but even on an open board it\ncould not move. It is what the rules call dead.</p>\n<p>Notice how it got there, because it is a rule worth knowing. A cob that reaches\nthe opponent's home by advancing would promote. This one did not promote, so it\ndid not arrive by advancing. It was a white cob, sitting on its own home point,\nstruck and flipped to black where it stood. A piece captured on its own base\nkeeps its rank instead of promoting, which is usually a mercy for the defender.\nHere it is the opposite: the flip stranded a cob at the one edge of the board\nwhere a cob is worthless.</p>\n<p>A dead piece is not quite nothing. If a player has no legal move anywhere but\nholds a dead cob, they are allowed to promote it on the spot so that it can move\nafter all, which is the only time you will see the <code>D2-D2</code> move. But a dead cob\nyou are not forced to promote is dead weight, and the endgame chapter shows how\noften a game is decided by which side is carrying more of it.</p>"
   }
  ]
 },
 {
  "slug": "05-striking",
  "title": "Striking",
  "blocks": [
   {
    "type": "md",
    "html": "<h1>Striking</h1>\n<p>You capture by arriving. Move a piece so that it lands next to an enemy, and\nthat enemy is struck: it stays on its point but flips to your colour and joins\nyour side. Nothing is removed from the board. A game of Tarati is won by\nconverting pieces, not by clearing them.</p>\n<p>One move can convert several pieces at once. Every enemy standing next to your\npiece's destination is struck together.</p>"
   },
   {
    "type": "figure",
    "caption": "Black plays C8-C7 and strikes on both sides at once, flipping the white pieces on C6 and D3.",
    "board": {
     "checkers": {
      "B3": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "B4": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C3": {
       "color": "BLACK",
       "isUpgraded": true
      },
      "C6": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C8": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "D1": {
       "color": "BLACK",
       "isUpgraded": true
      },
      "D2": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "D3": {
       "color": "WHITE",
       "isUpgraded": true
      }
     },
     "arrows": [
      [
       "C8",
       "C7"
      ]
     ],
     "highlightVertices": [
      "C6",
      "D3"
     ],
     "sub": null
    }
   },
   {
    "type": "md",
    "html": "<h2>The rule that makes Tarati Tarati</h2>\n<p>There is one condition on striking, and it is the whole character of the game:</p>\n<blockquote>\n<p>You cannot strike an enemy you were already standing next to. To capture a\npiece, you must arrive from a point that was not adjacent to it.</p>\n</blockquote>\n<p>Read that once and it sounds like a footnote. Watch it decide a single move and\nit stops being one.</p>"
   },
   {
    "type": "figure",
    "caption": "White's B1 sits next to two black pieces at once: A1 and B3.",
    "board": {
     "checkers": {
      "A1": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "B1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "B3": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "B4": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C8": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "D1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "D3": {
       "color": "BLACK",
       "isUpgraded": false
      }
     },
     "arrows": [],
     "highlightVertices": [
      "A1",
      "B3"
     ],
     "sub": null
    }
   },
   {
    "type": "md",
    "html": "<p>White's cob on B1 is touching two black pieces, A1 and B3. White plays B1-B2.\nNow B2 is next to both of them, so both look captured.</p>\n<p>Only B3 flips.</p>"
   },
   {
    "type": "figure",
    "caption": "After B1-B2: B3 has flipped to white, A1 is still black. Same move, opposite outcomes.",
    "board": {
     "checkers": {
      "A1": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "B2": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "B3": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "B4": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C8": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "D1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "D3": {
       "color": "BLACK",
       "isUpgraded": false
      }
     },
     "arrows": [],
     "highlightVertices": [
      "A1",
      "B3"
     ],
     "sub": null
    }
   },
   {
    "type": "md",
    "html": "<p>A1 was already B1's neighbour before the move, so the rule spares it: it stays\nblack. B3 was also B1's neighbour, and it flips anyway, because B2 reaches it\nalong a line that B1 did not command. Both pieces began adjacent to the mover.\nOne is taken and one is not, and the only difference is the geometry of the\narrival.</p>\n<p>This is why a strike is a thing you set up rather than a thing you spot. Sitting\nnext to an enemy accomplishes nothing. You have to step away and come back by\nanother road, or bring a different piece from distance.</p>\n<h2>What it costs the attacker</h2>\n<p>The pre-adjacency rule protects the defender, so it also disciplines the\nattacker. Crowding your pieces against the enemy line feels aggressive and\nachieves very little: every piece already in contact is a piece that cannot\ncapture what it touches. The threats on the board are the arrivals you have not\nmade yet, from points the enemy is not watching.</p>\n<p>The chapter on tactics puts numbers to this. For now, hold the shape of it: in\nTarati, standing next to something is the opposite of threatening it.</p>\n<h2>Promotion</h2>\n<p>A cob moves only forward, toward the enemy. Push one all the way onto the\nopponent's home base and it is promoted on arrival to a rok, which moves in any\ndirection for the rest of the game.</p>"
   },
   {
    "type": "figure",
    "caption": "A black cob reaches D1, deep in white's home base, and becomes a rok.",
    "board": {
     "checkers": {
      "A1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "B1": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "B3": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "B4": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C1": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C7": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C8": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "D2": {
       "color": "WHITE",
       "isUpgraded": false
      }
     },
     "arrows": [
      [
       "C1",
       "D1"
      ]
     ],
     "highlightVertices": [
      "D1"
     ],
     "sub": null
    }
   },
   {
    "type": "md",
    "html": "<p>Promotion changes what a piece is for. A cob is a one-way runner that must keep\nadvancing or die where it stands. A rok is free. It can retreat to defend, swing\nacross the board to strike from a new angle, and set up the arrivals that a cob\ncan never come back to make. Most decisive attacks in the corpus are led by a\nrok, for exactly that reason.</p>\n<p>There is one twist, and it protects the defender again. A cob struck while\nstanding on its own home base does not promote. It changes colour and\nstays a cob, stranded on the wrong side of the board, only able to move further\nfrom where it now needs to go. The endgame chapter returns to these stranded\npieces, because a game is often decided by them.</p>"
   }
  ]
 },
 {
  "slug": "06-openings",
  "title": "The opening",
  "blocks": [
   {
    "type": "md",
    "html": "<h1>The opening</h1>\n<p>Tarati's opening has a peculiar shape. For the first few moves nothing you do\ncan lose, and then, almost without warning, a single careless step can throw the\nwhole game. This chapter maps that transition and names the handful of opening\nideas worth knowing.</p>\n<p>Everything here comes from an exhaustive analysis: every distinct position\nreachable in the first ten moves was enumerated and evaluated. The claims are\nnot gathered from games, they are what the position is.</p>\n<h2>The first move does not matter, and there are only two of them</h2>\n<p>White has four legal opening moves, but the board's fold symmetry makes them two\nideas seen from either side. You can step a home cob <strong>inward</strong>, toward the hub,\nwith <code>C1-B1</code>. Or you can step it <strong>around the ring</strong>, along the outer\ndodecagon, with <code>C1-C12</code>. Its mirror, <code>C2-C3</code>, is the same idea.</p>\n<p>At perfect play the two are exactly equal, and so is every reply to them. Through\nthe first three moves for each side, the best move and the second-best move are\nworth precisely the same. You cannot go wrong yet.</p>\n<h2>Then it sharpens, fast</h2>\n<table>\n<thead>\n<tr>\n<th style=\"text-align: right;\">Move (ply)</th>\n<th style=\"text-align: right;\">Positions</th>\n<th style=\"text-align: right;\">Median cost of the 2nd-best move</th>\n<th style=\"text-align: center;\">Where a wrong move already loses a decided game</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td style=\"text-align: right;\">1</td>\n<td style=\"text-align: right;\">4</td>\n<td style=\"text-align: right;\">0</td>\n<td style=\"text-align: center;\">never</td>\n</tr>\n<tr>\n<td style=\"text-align: right;\">2</td>\n<td style=\"text-align: right;\">16</td>\n<td style=\"text-align: right;\">0.05</td>\n<td style=\"text-align: center;\">never</td>\n</tr>\n<tr>\n<td style=\"text-align: right;\">3</td>\n<td style=\"text-align: right;\">76</td>\n<td style=\"text-align: right;\">0.03</td>\n<td style=\"text-align: center;\">never</td>\n</tr>\n<tr>\n<td style=\"text-align: right;\">4</td>\n<td style=\"text-align: right;\">357</td>\n<td style=\"text-align: right;\">0.19</td>\n<td style=\"text-align: center;\">1% of positions</td>\n</tr>\n<tr>\n<td style=\"text-align: right;\">5</td>\n<td style=\"text-align: right;\">1,115</td>\n<td style=\"text-align: right;\">0.34</td>\n<td style=\"text-align: center;\">5%</td>\n</tr>\n<tr>\n<td style=\"text-align: right;\">6</td>\n<td style=\"text-align: right;\">3,544</td>\n<td style=\"text-align: right;\">0.45</td>\n<td style=\"text-align: center;\">6%</td>\n</tr>\n<tr>\n<td style=\"text-align: right;\">7</td>\n<td style=\"text-align: right;\">10,710</td>\n<td style=\"text-align: right;\">0.81</td>\n<td style=\"text-align: center;\">9%</td>\n</tr>\n</tbody>\n</table>\n<p>For three moves there is nothing to get wrong. By move five, one position in\ntwenty already has a losing second choice; by move seven, almost one in ten. The\nopening is a short, safe corridor that opens without warning into sharp ground.</p>\n<p>The practical lesson is about where to spend attention. Play the first few moves\nquickly. Start thinking hard at move five.</p>\n<h2>The openers are equal in theory, not in practice</h2>\n<p>That the two openings are equal at perfect play does not mean they play the same\nacross a board between real people. They do not. Measured over forty-eight\nthousand games between engines of matched strength, starting from the true\nopening position, the line the two players steer into is a real predictor of who\nwins. At perfect play all roads are level; in practice some are easier to walk.</p>\n<p>There are four common ways the first two moves go, and they score very\ndifferently.</p>\n<h3>The Inward Game: White steps in, Black holds the ring</h3>"
   },
   {
    "type": "sequence",
    "caption": "White steps inward and Black answers on the ring. White scores 51%.",
    "boards": [
     {
      "checkers": {
       "C1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C7": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C8": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D3": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D4": {
        "color": "BLACK",
        "isUpgraded": false
       }
      },
      "arrows": [
       [
        "C1",
        "B1"
       ]
      ],
      "highlightVertices": [],
      "sub": "1. C1-B1"
     },
     {
      "checkers": {
       "B1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C7": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C8": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D3": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D4": {
        "color": "BLACK",
        "isUpgraded": false
       }
      },
      "arrows": [
       [
        "C7",
        "C6"
       ]
      ],
      "highlightVertices": [],
      "sub": "1... C7-C6"
     },
     {
      "checkers": {
       "B1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C6": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C8": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D3": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D4": {
        "color": "BLACK",
        "isUpgraded": false
       }
      },
      "arrows": [],
      "highlightVertices": [],
      "sub": "position"
     }
    ]
   },
   {
    "type": "md",
    "html": "<p>White's inward step, met by Black developing along the ring, is White's most\nsuccessful practical opening: <strong>51% for White against 38% for Black</strong>, the rest\ndrawn. The inward cob heads straight for the middle, where it is exposed but also\nactive, and against a quieter ring reply that activity pays.</p>\n<h3>The Ring Game: both sides hold the ring</h3>"
   },
   {
    "type": "sequence",
    "caption": "Both sides step around the ring. Roughly balanced.",
    "boards": [
     {
      "checkers": {
       "C1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C7": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C8": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D3": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D4": {
        "color": "BLACK",
        "isUpgraded": false
       }
      },
      "arrows": [
       [
        "C1",
        "C12"
       ]
      ],
      "highlightVertices": [],
      "sub": "1. C1-C12"
     },
     {
      "checkers": {
       "C2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C7": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C8": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C12": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D3": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D4": {
        "color": "BLACK",
        "isUpgraded": false
       }
      },
      "arrows": [
       [
        "C7",
        "C6"
       ]
      ],
      "highlightVertices": [],
      "sub": "1... C7-C6"
     },
     {
      "checkers": {
       "C2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C6": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C8": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C12": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D3": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D4": {
        "color": "BLACK",
        "isUpgraded": false
       }
      },
      "arrows": [],
      "highlightVertices": [],
      "sub": "position"
     }
    ]
   },
   {
    "type": "md",
    "html": "<p>When both players open around the ring the game is quiet and close to even:\n<strong>45% White, 42% Black</strong>, and the highest draw rate of any line. This is the\nopening for a player who wants a solid, low-variance game.</p>\n<h3>The Counter-Thrust: Black answers the inward step in kind</h3>"
   },
   {
    "type": "sequence",
    "caption": "White steps around the ring; Black thrusts inward. Black scores 54%.",
    "boards": [
     {
      "checkers": {
       "C1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C7": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C8": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D3": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D4": {
        "color": "BLACK",
        "isUpgraded": false
       }
      },
      "arrows": [
       [
        "C1",
        "C12"
       ]
      ],
      "highlightVertices": [],
      "sub": "1. C1-C12"
     },
     {
      "checkers": {
       "C2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C7": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C8": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C12": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D3": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D4": {
        "color": "BLACK",
        "isUpgraded": false
       }
      },
      "arrows": [
       [
        "C7",
        "B4"
       ]
      ],
      "highlightVertices": [],
      "sub": "1... C7-B4"
     },
     {
      "checkers": {
       "B4": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C8": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C12": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D3": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D4": {
        "color": "BLACK",
        "isUpgraded": false
       }
      },
      "arrows": [],
      "highlightVertices": [],
      "sub": "position"
     }
    ]
   },
   {
    "type": "md",
    "html": "<p>The sharpest line, and the one most worth knowing. When White opens quietly on\nthe ring and Black thrusts a cob inward with <code>C7-B4</code>, it is <strong>Black</strong> who comes\nout ahead: <strong>54% to 36%</strong>. Black seizes the centre first and turns White's\npassivity against them. If you are White, this is the reply to respect, and a\nreason not to drift into a slow ring opening without a plan.</p>\n<h3>The Clash: both thrust inward</h3>"
   },
   {
    "type": "sequence",
    "caption": "Both players thrust inward at once. Sharp and balanced.",
    "boards": [
     {
      "checkers": {
       "C1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C7": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C8": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D3": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D4": {
        "color": "BLACK",
        "isUpgraded": false
       }
      },
      "arrows": [
       [
        "C1",
        "B1"
       ]
      ],
      "highlightVertices": [],
      "sub": "1. C1-B1"
     },
     {
      "checkers": {
       "B1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C7": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C8": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D3": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D4": {
        "color": "BLACK",
        "isUpgraded": false
       }
      },
      "arrows": [
       [
        "C7",
        "B4"
       ]
      ],
      "highlightVertices": [],
      "sub": "1... C7-B4"
     },
     {
      "checkers": {
       "B1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "B4": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C8": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "D3": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D4": {
        "color": "BLACK",
        "isUpgraded": false
       }
      },
      "arrows": [],
      "highlightVertices": [],
      "sub": "position"
     }
    ]
   },
   {
    "type": "md",
    "html": "<p>When both players thrust inward the game is sharp and roughly even, <strong>46% to\n47%</strong>, with the fewest draws of any line. Two cobs race for the middle and the\ngame is decided by who handles the resulting collisions better. This is the\nfighting opening.</p>\n<h2>What the four lines have in common</h2>\n<p>Notice the thread. The inward thrust is the aggressive move wherever it appears:\nit wins for White in the Inward Game, it wins for Black in the Counter-Thrust,\nand it produces the sharpest, least drawish play in the Clash. Stepping toward\nthe centre first is a genuine practical edge, but only if you use the piece.\nThe board-control chapter shows how quickly a piece left sitting in the\nmiddle is struck. The inward step is a commitment to attack, not a place to\npark. Take it when you intend to follow up, and punish an opponent who takes it\nand then hesitates.</p>"
   }
  ]
 },
 {
  "slug": "07-board-control",
  "title": "Board control",
  "blocks": [
   {
    "type": "md",
    "html": "<h1>Board control</h1>\n<p>Every player new to Tarati reaches for the centre. The hub touches all six\ninner points, more than any other square, so it looks like the place to be. It\nis the single most expensive mistake in the game.</p>\n<h2>The centre is a trap</h2>\n<p>Measured across roughly ninety thousand positions, holding the hub is worth\nabout <strong>three-quarters of a piece against you</strong>. Not a small edge, not a wash:\na liability the size of most captures. The inner hexagon around it is nearly as\nbad. The one part of the board that looks strong is the part you should least\nwant to occupy.</p>\n<p>This is not a quirk of one engine's taste. It falls straight out of the\nstriking rule. A piece is captured when an enemy arrives next to it from a point\nit did not already command, and the more lines meet at a square, the more\ndirections that arrival can come from. The hub, with six neighbours, can be\nattacked from all of them. A home point, with two, almost never can.</p>\n<p>Count it directly in real games and the pattern is stark:</p>\n<table>\n<thead>\n<tr>\n<th>Point type</th>\n<th style=\"text-align: center;\">Connections</th>\n<th style=\"text-align: center;\">Struck, per visit</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td>Home points (D)</td>\n<td style=\"text-align: center;\">2</td>\n<td style=\"text-align: center;\">1.0%</td>\n</tr>\n<tr>\n<td>Outer ring (C)</td>\n<td style=\"text-align: center;\">3\u20134</td>\n<td style=\"text-align: center;\">~3%</td>\n</tr>\n<tr>\n<td>Inner hexagon (B)</td>\n<td style=\"text-align: center;\">5</td>\n<td style=\"text-align: center;\">5.0%</td>\n</tr>\n<tr>\n<td>The hub (A1)</td>\n<td style=\"text-align: center;\">6</td>\n<td style=\"text-align: center;\">5.2%</td>\n</tr>\n</tbody>\n</table>\n<p>A piece on the hub is struck <strong>five times as often</strong> as one tucked on a home\npoint. Put a piece in the centre and you have placed it where the enemy has the\nmost ways to reach it.</p>"
   },
   {
    "type": "figure",
    "caption": "A white piece sits on the hub. Black arrives at B4 from a distant point and strikes it.",
    "board": {
     "checkers": {
      "A1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "B5": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C2": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C7": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C8": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "D1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "D3": {
       "color": "BLACK",
       "isUpgraded": false
      }
     },
     "arrows": [
      [
       "C8",
       "B4"
      ]
     ],
     "highlightVertices": [
      "A1"
     ],
     "sub": null
    }
   },
   {
    "type": "figure",
    "caption": "The hub piece has flipped to black. The centre gave White nothing and cost a piece.",
    "board": {
     "checkers": {
      "A1": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "B4": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "B5": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C2": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C7": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "D1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "D3": {
       "color": "BLACK",
       "isUpgraded": false
      }
     },
     "arrows": [],
     "highlightVertices": [
      "A1"
     ],
     "sub": null
    }
   },
   {
    "type": "md",
    "html": "<p>The lesson is not \"never touch the centre\". A rok can pass through it to reach\nthe far side, and a fleeting visit that sets up a capture can be worth the risk.\nThe lesson is that the centre is a road, not a home. Park a piece there and it\nis a target.</p>\n<h2>What is worth having instead</h2>\n<p>If not the centre, then what? The same measurement that condemns the hub ranks\nevery other feature of a position, and one of them stands out.</p>\n<p><strong>Mobility.</strong> The number of legal moves you have, over and above your\nopponent's, is worth about ten times what a casual eye would guess. Room to move\nis close to the most valuable thing a position can give you, behind only\nmaterial itself. This follows the same logic as the centre in reverse: a player\nwith many moves can choose the arrival that captures, while a player pinned to a\nfew is forced into moves that expose pieces. In Tarati you win by keeping your\noptions open and closing down your opponent's.</p>\n<p><strong>Material.</strong> A piece is worth a piece, and a rok is worth\nabout a piece and a half, which matches intuition. What does not match intuition\nis how much the positional factors move around that baseline. A one-piece\nmaterial lead is real, but a player who has buried a piece in the centre and\ncramped their own movement can be behind while nominally ahead on the count.</p>\n<p>Put the two ideas together and Tarati's version of good play comes into focus.\nKeep your pieces where they cannot be easily reached, on the outer ring and near\nhome. Keep them mobile. Push into the centre only to arrive somewhere, never to\nstay. Make your opponent be the one with a piece stuck on the hub and nowhere\nto go.</p>\n<h2>Setting up a strike</h2>\n<p>Mobility is not just safety, it is the raw material of attack. Because you can\nonly capture a piece by arriving from a line you did not already command, a\nstrike usually has to be arranged a move in advance: you cannot take what you are\nalready touching, so you step away and come back by another road. A rok, free to\nmove in any direction, is the piece that can do this.</p>"
   },
   {
    "type": "sequence",
    "caption": "A black rok slides from C2 to C1, threatening nothing yet. After White replies, the rok swings to C12 and strikes the white piece on B6, which it could not have reached a move earlier.",
    "boards": [
     {
      "checkers": {
       "B1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "B2": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "B4": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "B5": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "B6": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C2": {
        "color": "BLACK",
        "isUpgraded": true
       },
       "C5": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D3": {
        "color": "WHITE",
        "isUpgraded": true
       }
      },
      "arrows": [
       [
        "C2",
        "C1"
       ]
      ],
      "highlightVertices": [],
      "sub": "rok slides to C1"
     },
     {
      "checkers": {
       "B1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "B2": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "B4": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "B5": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "B6": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C1": {
        "color": "BLACK",
        "isUpgraded": true
       },
       "C5": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "D3": {
        "color": "WHITE",
        "isUpgraded": true
       }
      },
      "arrows": [
       [
        "B4",
        "C7"
       ]
      ],
      "highlightVertices": [],
      "sub": "White replies"
     },
     {
      "checkers": {
       "B1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "B2": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "B5": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "B6": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C1": {
        "color": "BLACK",
        "isUpgraded": true
       },
       "C5": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "C7": {
        "color": "WHITE",
        "isUpgraded": true
       },
       "D3": {
        "color": "WHITE",
        "isUpgraded": true
       }
      },
      "arrows": [
       [
        "C1",
        "C12"
       ]
      ],
      "highlightVertices": [
       "B6"
      ],
      "sub": "and strikes B6"
     }
    ]
   },
   {
    "type": "md",
    "html": "<p>The first move looks idle. The rok slides one square along the ring and captures\nnothing. What it has done is reposition to a point from which the target is now\ntwo steps away rather than one, so that the follow-up arrives from distance and\nthe pre-adjacency rule permits the capture. The move that does the damage is the\nsecond one; the first is what made it legal.</p>\n<p>This is the shape of most attacks in Tarati. The threatening move is rarely the\none that looks aggressive. It is the quiet repositioning a move earlier that\nturns a piece you cannot yet take into one you can. When you want a capture, do\nnot ask which of your pieces is next to the target. Ask which of them can get\nnext to it from somewhere new.</p>"
   }
  ]
 },
 {
  "slug": "08-endgame",
  "title": "The endgame",
  "blocks": [
   {
    "type": "md",
    "html": "<h1>The endgame</h1>\n<p>You do not win Tarati by clearing the board. You win by owning it. Every enemy\npiece you strike changes colour and joins you, so the natural end of a game is\nnot an empty board but a board that has turned entirely to your side. Knowing\nhow that conversion arrives, and how it can stall, is what wins close games.</p>\n<h2>How games end</h2>\n<p>Across more than a million recorded games, the endings break down like this:</p>\n<table>\n<thead>\n<tr>\n<th>How the game ended</th>\n<th style=\"text-align: center;\">Share</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td>Total conversion (every piece one colour)</td>\n<td style=\"text-align: center;\">82.9%</td>\n</tr>\n<tr>\n<td>Opponent has no legal move</td>\n<td style=\"text-align: center;\">10.6%</td>\n</tr>\n<tr>\n<td>Threefold repetition (draw)</td>\n<td style=\"text-align: center;\">6.2%</td>\n</tr>\n<tr>\n<td>Move limit or fifty-move draw</td>\n<td style=\"text-align: center;\">0.3%</td>\n</tr>\n</tbody>\n</table>\n<p>More than four games in five end by total conversion. The board does not empty;\nit flips. This single fact shapes the whole endgame: your target is not the\nenemy's last piece but the enemy's last <strong>point</strong>, and every capture brings that\nend one piece closer while making you stronger and them weaker at the same time.</p>\n<h2>Conversion cascades</h2>\n<p>Because a capture flips a piece rather than removing it, and because one move can\nstrike every enemy standing next to its destination, the end often arrives all\nat once. A single well-placed arrival can turn three pieces over and finish the\ngame on the spot.</p>"
   },
   {
    "type": "sequence",
    "caption": "White plays B2-A1 and strikes B4, B5 and B6 together. Every black piece flips at once and the game is over.",
    "boards": [
     {
      "checkers": {
       "B2": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "B3": {
        "color": "WHITE",
        "isUpgraded": true
       },
       "B4": {
        "color": "BLACK",
        "isUpgraded": false
       },
       "B5": {
        "color": "BLACK",
        "isUpgraded": true
       },
       "B6": {
        "color": "BLACK",
        "isUpgraded": true
       },
       "C7": {
        "color": "WHITE",
        "isUpgraded": true
       },
       "C8": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C11": {
        "color": "WHITE",
        "isUpgraded": false
       }
      },
      "arrows": [
       [
        "B2",
        "A1"
       ]
      ],
      "highlightVertices": [
       "B4",
       "B5",
       "B6"
      ],
      "sub": "White to play"
     },
     {
      "checkers": {
       "A1": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "B3": {
        "color": "WHITE",
        "isUpgraded": true
       },
       "B4": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "B5": {
        "color": "WHITE",
        "isUpgraded": true
       },
       "B6": {
        "color": "WHITE",
        "isUpgraded": true
       },
       "C7": {
        "color": "WHITE",
        "isUpgraded": true
       },
       "C8": {
        "color": "WHITE",
        "isUpgraded": false
       },
       "C11": {
        "color": "WHITE",
        "isUpgraded": false
       }
      },
      "arrows": [],
      "highlightVertices": [],
      "sub": "all white: game over"
     }
    ]
   },
   {
    "type": "md",
    "html": "<p>Here a white cob steps to the hub and lands beside three black pieces at once.\nNone of them was adjacent to the cob before it moved, so all three are struck,\nall three flip, and the board is entirely white. The game ends in a single move.</p>\n<p>This is the centre used correctly, and it is worth setting beside the warning in\nthe board-control chapter. Sitting on the hub is a liability because it can be\nstruck from six directions. Passing through the hub to strike from it is\ndevastating for exactly the same reason: from the centre you stand next to\neverything. The rule that makes the hub dangerous to occupy makes it lethal to\nattack from. The centre is a place you arrive, strike, and win, not a place you\nwait.</p>\n<h2>The rok wins endgames</h2>\n<p>The piece that delivers these conversions is almost always a rok. A cob can only\npush forward, so once it has advanced it can never turn to strike a piece behind\nor beside it, and late in the game the pieces you need to convert are scattered\nin every direction. A rok goes anywhere. It can cross the board to reach a lone\nholdout, retreat to flip a piece that slipped past, and, above all, leave a\nsquare and return to strike a target it was already next to. Promote a cob to a\nrok and you have turned a one-way runner into the tool that finishes games. If\nyou are ahead as the board empties, get a rok and hunt with it.</p>\n<h2>Jamming: the game's quiet decider</h2>\n<p>Not every advantage converts. The forward-only cob has a fatal weakness, and in\nclose endgames it decides more games than any tactic.</p>"
   },
   {
    "type": "figure",
    "caption": "A black cob is stranded on D2, at the far edge of White's home. It has no square ahead of it and can never move again.",
    "board": {
     "checkers": {
      "B1": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "B4": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C2": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C7": {
       "color": "WHITE",
       "isUpgraded": true
      },
      "D1": {
       "color": "BLACK",
       "isUpgraded": true
      },
      "D2": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "D3": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "D4": {
       "color": "WHITE",
       "isUpgraded": false
      }
     },
     "arrows": [],
     "highlightVertices": [
      "D2"
     ],
     "sub": null
    }
   },
   {
    "type": "md",
    "html": "<p>A cob that reaches the far edge with no point ahead of it is <strong>dead</strong>. It cannot\nadvance, because there is nothing to advance to, and it cannot retreat, because\ncobs never do. It sits on the board as your piece in name only, contributing\nnothing, blocking nothing useful, waiting to be counted against you.</p>\n<p>Measured across the labelled positions, a dead cob is worth about a third of a\npiece <strong>against</strong> its owner even while it still counts as material. You can be\nahead on the piece count and behind in reality because one of your pieces has\nwalked into a corner it can never leave. The most common way this happens is\ncruel: a cob captured while sitting on its own home base keeps its rank instead\nof promoting, and is reborn as an enemy cob at the one edge of the board where a\ncob is worthless.</p>\n<p>The practical lessons are two. First, watch where your cobs are heading. A cob\ndriven to the far edge without a promotion square waiting is a cob you are about\nto lose the use of. Second, when you are defending, you can aim for this: force\nyour opponent's cobs forward into dead ends, and a material deficit can turn into\na win as their pieces strand themselves one by one.</p>\n<h2>Playing the endgame</h2>\n<p>Draw the threads together. You are trying to convert the whole board, so keep\nyour captures coming and count points, not pieces. A rok is your conversion\nengine, so make one and use its freedom. The hub and the inner ring, dangerous\nto occupy, are where your decisive arrivals come from, so strike through the\ncentre rather than sitting in it. And keep one eye on the forward-only cobs, both\nyours and your opponent's, because the game is often decided not by a brilliant\ncapture but by whose pieces quietly ran out of road.</p>"
   }
  ]
 },
 {
  "slug": "09-levels",
  "title": "Where games are lost",
  "blocks": [
   {
    "type": "md",
    "html": "<h1>Where games are lost</h1>\n<p>The fastest way to improve is to know where you are bleeding. Because the engine\nscored every move in more than a hundred thousand real positions, we can watch\nexactly where players of each strength go wrong, and the picture is clear enough\nto turn into advice.</p>\n<p>Two numbers describe a player. How often they find the best move, and how often\nthey throw away more than a full piece in a single move. Here is how both change\nwith strength, broken down by phase of the game.</p>\n<h2>The blunder map</h2>\n<p>Share of moves that give away more than a piece:</p>\n<table>\n<thead>\n<tr>\n<th>Level</th>\n<th style=\"text-align: center;\">Opening</th>\n<th style=\"text-align: center;\">Middlegame</th>\n<th style=\"text-align: center;\">Endgame</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td>Beginner</td>\n<td style=\"text-align: center;\">24.9%</td>\n<td style=\"text-align: center;\">17.4%</td>\n<td style=\"text-align: center;\">20.3%</td>\n</tr>\n<tr>\n<td>Intermediate</td>\n<td style=\"text-align: center;\">19.7%</td>\n<td style=\"text-align: center;\">11.0%</td>\n<td style=\"text-align: center;\">16.3%</td>\n</tr>\n<tr>\n<td>Strong</td>\n<td style=\"text-align: center;\">12.4%</td>\n<td style=\"text-align: center;\">7.0%</td>\n<td style=\"text-align: center;\">10.0%</td>\n</tr>\n<tr>\n<td>Expert</td>\n<td style=\"text-align: center;\">2.1%</td>\n<td style=\"text-align: center;\">1.6%</td>\n<td style=\"text-align: center;\">3.7%</td>\n</tr>\n</tbody>\n</table>\n<p>Read down the columns and two facts jump out.</p>\n<p><strong>Beginners lose in the opening.</strong> A quarter of a beginner's opening moves throw\naway a piece, far more than in any other phase. This is the trap the openings\nchapter warned about made visible: the first few moves feel safe, so a weak\nplayer relaxes, and then the position sharpens and punishes the habit. If you are\nstarting out, the single highest-value thing you can do is slow down at move\nfive. The moves that feel free are the ones costing you games.</p>\n<p><strong>Everyone loses in the endgame.</strong> Look at the endgame column. At every level it\nsits above the middlegame, and for the strongest players it is the worst phase\noutright: an expert throws a piece in the endgame nearly twice as often as in the\nopening. Conversion and jamming are hard to calculate. Whose cob strands first,\nwhether a rok arrives in time, whether a strike cascades or fizzles, these are\nconcrete questions that reward depth of calculation, and they are the last thing\na strong player masters.</p>\n<p>That column is also the one that moved most when this guide's analysis engine was\nrebuilt to play out exchanges before judging a position. The old engine stopped\nmid-exchange and quietly forgave endgame errors it could not see; every endgame\nfigure above rose when it was fixed. If anything, the endgame is harder than the\nearlier draft of this chapter said.</p>\n<h2>Everyone is sharpest in the middle</h2>\n<p>The other pattern is that the middlegame is where every level plays best. Share\nof moves that match the engine's first choice:</p>\n<table>\n<thead>\n<tr>\n<th>Level</th>\n<th style=\"text-align: center;\">Opening</th>\n<th style=\"text-align: center;\">Middlegame</th>\n<th style=\"text-align: center;\">Endgame</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td>Beginner</td>\n<td style=\"text-align: center;\">43%</td>\n<td style=\"text-align: center;\">52%</td>\n<td style=\"text-align: center;\">41%</td>\n</tr>\n<tr>\n<td>Intermediate</td>\n<td style=\"text-align: center;\">54%</td>\n<td style=\"text-align: center;\">65%</td>\n<td style=\"text-align: center;\">54%</td>\n</tr>\n<tr>\n<td>Strong</td>\n<td style=\"text-align: center;\">65%</td>\n<td style=\"text-align: center;\">74%</td>\n<td style=\"text-align: center;\">65%</td>\n</tr>\n<tr>\n<td>Expert</td>\n<td style=\"text-align: center;\">85%</td>\n<td style=\"text-align: center;\">90%</td>\n<td style=\"text-align: center;\">82%</td>\n</tr>\n</tbody>\n</table>\n<p>Every row peaks in the middle. Once the pieces are developed and the threats are\non the board, the right move is easiest to see. The hard phases are the two\nedges: the opening, where the position is quiet and the danger is hidden, and the\nendgame, where the position is sharp and the calculation is deep.</p>\n<h2>What to work on</h2>\n<p>The measurements turn into a simple ladder of advice.</p>\n<p>If you lose games quickly and cannot say why, you are almost certainly a beginner\nlosing in the opening. Slow down through moves five to ten and stop giving pieces\naway in the quiet, and you will climb a whole level on that alone.</p>\n<p>If your openings are sound but you cannot finish, study the endgame chapter. The\nskills that separate a strong player from an expert are conversion and jamming,\nthe two ideas that stay hard no matter how good you get. Learn to count who runs\nout of moves, and to lead your attacks with a rok, and the games you used to let\nslip will start to close.</p>\n<h2>And past that</h2>\n<p>The blunder map runs out of resolution at the top. Every level in that table is\nstill losing games to moves that can be found by looking one or two moves ahead,\nand the advice above is really advice about looking more carefully.</p>\n<p>Above that point the errors change character. They stop being moves you failed to\ncalculate and start being moves you never considered. We measured how deep a\nsearch has to go before it finds each kind of winning move, and the split is\nsharp: strikes and escapes are found within two ply about eighty per cent of the\ntime. The quiet winners, the threat-build and the shut, are found within\ntwo ply less than half the time, and one in six needs seven ply or more.</p>\n<p>Seven ply is not something you calculate at a board. It is something you\nrecognise. That is what the next five chapters are for: not harder calculation,\nbut a short list of things worth seeing at a glance, each one measured rather\nthan asserted.</p>"
   }
  ]
 },
 {
  "slug": "10-the-eight",
  "title": "The eight",
  "blocks": [
   {
    "type": "md",
    "html": "<h1>The eight</h1>\n<p>Everything from here on assumes you already play a reasonable game: you know how\nthe pieces move, you can see a strike coming, and you have stopped parking pieces\non the hub. What follows is the next layer, and it starts with a fact about the\ngame that almost nobody notices in their first hundred games.</p>\n<h2>Nobody is ever captured</h2>\n<p>Count the pieces at the end of any Tarati game. There are eight. Count them at\nmove forty. Eight. At move two hundred, if the game lasts that long, still eight.</p>\n<p>A strike does not remove a piece from the board. It turns it around. The piece\nthat was yours a moment ago is standing on exactly the same point, and it is now\nyour opponent's. Replaying three hundred complete games move by move, the board\nheld eight pieces in all 11,582 positions that occurred, without exception.</p>\n<p>This is not a curiosity. It changes the arithmetic of the whole game.</p>\n<h2>A strike is worth two pieces, not one</h2>\n<p>In a game where captures remove pieces, taking one puts you one ahead. In Tarati,\ntaking one puts you <strong>two</strong> ahead, because the same act that adds a piece to your\nside subtracts one from theirs. Five against three is not \"one up\". It is a\ntwo-piece gap on an eight-piece board, a quarter of everything there is.</p>\n<p>You can see this in what deep analysis pays for material. The guide's evaluation\nwas hand-set at 100 points per piece. Fitted against what a twelve-ply search\nactually thinks positions are worth, across 92,769 of them, the true figure comes\nout at <strong>136</strong>. The hand-set number was measuring the piece. The fitted number is\nmeasuring the swing.</p>\n<p>So the first correction to a club player's instincts is this: <strong>every strike is\nworth roughly twice what it looks like.</strong> A line that wins a piece at the cost of\nletting one go is not level. It is a wash only if both strikes land; if yours\nlands and theirs does not, you have moved the position by a quarter of the board.</p>\n<h2>Some strikes take two</h2>\n<p>The strike rule fires on <em>every</em> enemy adjacent to the square you arrive on that\nyou were not already beside. Usually that is one piece. Sometimes it is more.</p>\n<p>Across 66,038 strikes in the corpus:</p>\n<table>\n<thead>\n<tr>\n<th style=\"text-align: center;\">Pieces flipped</th>\n<th style=\"text-align: center;\">Share</th>\n<th style=\"text-align: center;\">Swing</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td style=\"text-align: center;\">one</td>\n<td style=\"text-align: center;\">82.7%</td>\n<td style=\"text-align: center;\">2</td>\n</tr>\n<tr>\n<td style=\"text-align: center;\">two</td>\n<td style=\"text-align: center;\">16.8%</td>\n<td style=\"text-align: center;\">4</td>\n</tr>\n<tr>\n<td style=\"text-align: center;\">three</td>\n<td style=\"text-align: center;\">0.5%</td>\n<td style=\"text-align: center;\">6</td>\n</tr>\n</tbody>\n</table>\n<p>A double strike moves four pieces, <strong>half the board</strong>, in a single move. It is\nnot a rare flourish either: one strike in six is a double. If you are only ever\nlooking for the single, you are missing the move that decides games.</p>"
   },
   {
    "type": "figure",
    "caption": "White to move, three pieces against five, and apparently losing. B1 is empty, and standing on it would put White newly beside both A1 and B2. C2-B1 takes them both: five against three the other way, in one move.",
    "board": {
     "checkers": {
      "A1": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "B2": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "B4": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "B5": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C2": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C3": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C8": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "D1": {
       "color": "WHITE",
       "isUpgraded": false
      }
     },
     "arrows": [
      [
       "C2",
       "B1"
      ]
     ],
     "highlightVertices": [
      "A1",
      "B2"
     ],
     "sub": null
    }
   },
   {
    "type": "md",
    "html": "<p>The way to find doubles is to stop looking at enemy pieces one at a time and\nstart looking at <em>empty squares</em>. Ask of each empty point next to you: if I stood\nthere, how many enemies would I be newly beside? That question finds doubles.\nScanning targets one by one does not.</p>\n<h2>What this means for how you count</h2>\n<p>Three habits follow, and they are the difference between counting like a beginner\nand counting like a strong player.</p>\n<p><strong>Count to eight, not from zero.</strong> \"I am a piece up\" is a chess sentence. The\nTarati sentence is \"five to three\". The denominator is fixed, so a lead is always\na fraction of a known whole, and you always know exactly how far from the end you\nare: eight to nothing is the win.</p>\n<p><strong>Price a strike at two.</strong> When you weigh a line that wins one and loses one,\nremember both legs are doubled, so they still cancel. But a line that wins one\nand loses nothing is worth twice what you would guess, and so is the reverse.\nThis is why a single careless move loses games in Tarati faster than in games\nwhere captures merely subtract.</p>\n<p><strong>Strikes are common, so tempo is cheap and safety is not.</strong> Thirty-eight per cent\nof all moves played in the corpus strike something, and twenty per cent of all\n<em>legal</em> moves do. In a position with eight legal moves, you should expect one or\ntwo of them to take a piece, for you and for your opponent on the reply. There\nis no quiet phase in this game. There are only positions where you have not yet\nfound the strike.</p>\n<p>The rest of these chapters are about the two questions that follow from that.\nWhich of my pieces can be taken, and how do I stop it? And when nothing can be\ntaken, what is the move?</p>"
   }
  ]
 },
 {
  "slug": "11-doors",
  "title": "Doors",
  "blocks": [
   {
    "type": "md",
    "html": "<h1>Doors</h1>\n<p>The board-control chapter told you the centre is a trap and gave you the reason:\nthe hub has six neighbours, so it can be attacked from six directions. That is\ntrue, and it is also the beginning of something much more useful. The same idea,\nsharpened, becomes a number you can count at the board in about two seconds, and\nit tells you which of your pieces is actually in danger.</p>\n<h2>The only way in</h2>\n<p>A strike happens when an enemy <strong>arrives</strong> on a point next to your piece. Arrives.\nNot sits, not aims, not threatens from a distance. Something has to physically\nmove onto an empty square beside you.</p>\n<p>So count the empty squares beside your piece. Call them its <strong>doors</strong>. If a piece\nhas no empty neighbour, no enemy can arrive next to it, and the piece cannot be\nstruck at all. Not by a clever move, not by a rok, not by anything.</p>\n<p>That is not a low probability. It is zero, and it is zero by the rule rather than\nby luck. Across 144,378 pieces observed in real positions, the ones with no doors\nwere takeable <strong>0 times out of 3,425</strong>.</p>\n<h2>The whole risk, in one number</h2>\n<p>Add the doors up and the danger scales almost linearly. Here is the chance that a\npiece can be taken by the opponent's very next move, by how many doors it has:</p>\n<table>\n<thead>\n<tr>\n<th style=\"text-align: center;\">Doors</th>\n<th style=\"text-align: center;\">Chance it can be taken now</th>\n<th style=\"text-align: center;\">Positions</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td style=\"text-align: center;\">0</td>\n<td style=\"text-align: center;\"><strong>0.0%</strong></td>\n<td style=\"text-align: center;\">3,425</td>\n</tr>\n<tr>\n<td style=\"text-align: center;\">1</td>\n<td style=\"text-align: center;\">11.1%</td>\n<td style=\"text-align: center;\">23,865</td>\n</tr>\n<tr>\n<td style=\"text-align: center;\">2</td>\n<td style=\"text-align: center;\">19.1%</td>\n<td style=\"text-align: center;\">49,190</td>\n</tr>\n<tr>\n<td style=\"text-align: center;\">3</td>\n<td style=\"text-align: center;\">30.4%</td>\n<td style=\"text-align: center;\">43,249</td>\n</tr>\n<tr>\n<td style=\"text-align: center;\">4</td>\n<td style=\"text-align: center;\">41.0%</td>\n<td style=\"text-align: center;\">19,639</td>\n</tr>\n<tr>\n<td style=\"text-align: center;\">5</td>\n<td style=\"text-align: center;\">51.5%</td>\n<td style=\"text-align: center;\">4,781</td>\n</tr>\n<tr>\n<td style=\"text-align: center;\">6</td>\n<td style=\"text-align: center;\">81.7%</td>\n<td style=\"text-align: center;\">229</td>\n</tr>\n</tbody>\n</table>\n<p>Every extra door costs you roughly ten percentage points of safety. A piece with\nfour doors is in danger four times out of ten, right now, on the very next move.\nA piece with one door is nearly safe.</p>\n<p>This is the number the \"centre is a trap\" rule was approximating. A piece on the\nhub can be taken on the next move <strong>37.3%</strong> of the time; a piece on a home point,\n<strong>10.8%</strong>. But degree alone gets it wrong in places. The four home-corner points\nhave four connections yet sit at 19%, safer than the three-connection points at\n24%, because their neighbours are squares the enemy struggles to reach. Doors\ncount what actually matters: not how many lines meet at a square, but how many of\nthem are currently standing open.</p>"
   },
   {
    "type": "figure",
    "caption": "Both marked pieces are white cobs and both count for one on a material count. A1 has four doors and Black can take it on this move. C6 has none, and nothing on the board can touch it. Look again at C6 when you reach the end of this chapter.",
    "board": {
     "checkers": {
      "A1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "B1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "B3": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C5": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C6": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C7": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C10": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C11": {
       "color": "BLACK",
       "isUpgraded": false
      }
     },
     "arrows": [],
     "highlightVertices": [
      "A1",
      "C6"
     ],
     "sub": null
    }
   },
   {
    "type": "md",
    "html": "<h2>Learning to see it</h2>\n<p>At the board, the count is fast. Look at a piece, count the empty points touching\nit, and that digit is its risk. Two habits fall out.</p>\n<p><strong>Before you move a piece, count the doors of the square you are moving to.</strong>\nNot the square it is leaving. Beginners evaluate a move by what it attacks;\nstrong players also evaluate it by what it exposes. Moving from a two-door square\nto a four-door square is handing your opponent twenty points of probability for\nfree, and you will not feel it until the piece is gone.</p>\n<p><strong>When you want to attack, count your opponent's doors and go where they are\nwidest.</strong> A three-door enemy piece is one you have a real chance of reaching. A\none-door enemy piece usually is not worth planning around, and the move that\nwould take it is often better spent elsewhere.</p>\n<h2>The catch, and it is a big one</h2>\n<p>A piece with no doors cannot be struck. A piece with no doors also cannot move.</p>\n<p>That is the same sentence. The empty squares beside your piece are simultaneously\nthe routes an enemy uses to reach it and the routes it uses to go anywhere. Total\nsafety and total paralysis are the identical position, and in a game you lose by\nhaving no legal move, walling yourself in is not a defence. It is a slow way of\nlosing.</p>\n<p>Go back to the diagram. C6 cannot be struck by anything on the board, and C6\ncannot move. It is not a fortress, it is a piece White no longer owns in any\nsense that matters. White has two such cobs in that position and four legal moves\nin total, against Black's five.</p>\n<p>So doors are not something to minimise. They are something to <strong>price</strong>. A piece\nwith one door is cheap to keep and hard to use. A piece with four is useful and\nexpensive. Good play in the middlegame is mostly a matter of paying that price\nwhere it buys you something and refusing to pay it where it does not. The\nendgame chapter is about what happens when a whole side runs out of the ability\nto pay it at all.</p>"
   }
  ]
 },
 {
  "slug": "12-contact",
  "title": "There is no quiet approach",
  "blocks": [
   {
    "type": "md",
    "html": "<h1>There is no quiet approach</h1>\n<p>Here is a rule you will not find in the rulebook, because it is not a rule. It is\na consequence, and it is the single most surprising thing about how Tarati\nactually plays.</p>\n<p><strong>You cannot walk up to an enemy piece. If you arrive beside one, you take it.</strong></p>\n<p>Read the striking rule again and it is obvious in hindsight. A move strikes every\nenemy adjacent to your destination that was not already adjacent to your origin.\n\"An enemy adjacent to where I am going that was not adjacent to where I was\" is\njust a long way of saying \"an enemy I have newly come into contact with\". The\ncondition for striking and the condition for approaching are the same condition.</p>\n<p>We checked it exhaustively. Of 52,267 legal moves examined across the corpus,\n10,450 made new contact with an enemy piece. The number of those that struck\nnothing: <strong>zero</strong>. And running it the other way, across 39,107 actual strikes in\nrecorded games, the number delivered by a piece that was already touching its\nvictim: <strong>zero</strong>.</p>\n<h2>What follows: the standoff</h2>\n<p>If contact can only be created by a strike, then contact that already exists is\npermanent, and it is <em>safe</em>.</p>\n<p>Your piece and an enemy piece are standing next to each other. Can it take you?\nNo. It is already beside you, so moving to any square still beside you strikes\nnothing; the rule requires new contact and there is none to be had. Can you take\nit? Also no, for exactly the same reason.</p>\n<p>Two pieces in contact are frozen with respect to each other. Neither can ever\nharm the other from where they stand. They can shuffle around each other all day.</p>"
   },
   {
    "type": "figure",
    "caption": "White's cob on B4 is touching two black cobs at once, on B5 and C8. It cannot take either of them and neither can take it. In this whole position, four pieces apiece, there is not a single move for either side that strikes anything.",
    "board": {
     "checkers": {
      "B4": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "B5": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C2": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "C8": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C11": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "D1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "D2": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "D4": {
       "color": "BLACK",
       "isUpgraded": false
      }
     },
     "arrows": [],
     "highlightVertices": [
      "B4",
      "B5",
      "C8"
     ],
     "sub": null
    }
   },
   {
    "type": "md",
    "html": "<p>This has a defensive use that most players never find. If a piece of yours is\nbeing hunted, one way out is not to run. It is to be <em>taken into contact</em> by the\npiece hunting it, or to accept a position where you are already touching. An\nattacker in contact with you has spent itself. It must leave, go somewhere it is\nno longer adjacent, and come back by a different road before it can hurt you, and\nthat takes at least two moves you can see coming.</p>\n<h2>What follows: attacks are built from a distance</h2>\n<p>The other half is what the board-control chapter called setting up a strike, and\nnow we can say exactly why it works that way.</p>\n<p>To take a piece, you must arrive from somewhere you were not. So the piece that\ntakes it is never the piece already next to it. It is a piece <strong>two steps away</strong>,\nor a piece that steps back and then in. Every attack in Tarati is launched from\njust outside contact.</p>\n<p>That gives you a clean way to read a position for danger. Forget your opponent's\npieces that are touching yours, which are inert against you. Look at the ones\nsitting one square off, with a path to an empty point beside your piece. Those\nare the live threats, and they are the ones a club player consistently fails to\nsee, because they do not look like they are attacking anything.</p>\n<p><strong>The two-step scan.</strong> For each of your pieces that matters, look at its doors.\nFor each door, ask which enemy pieces can reach that square in one move, and\nwhether any of them are not already touching your piece. If the answer is yes,\nthe piece is takeable next move. If the answer is no, it is safe this turn no\nmatter how threatening the position looks.</p>\n<h2>What follows: contact is the residue of a fight</h2>\n<p>There is one more consequence, and it is useful for reading a board you have just\nsat down at.</p>\n<p>Since no one can walk into contact, every adjacency between opposing pieces on\nthe board arrived there one of three ways: a strike happened there, a piece slid\nfrom one square beside the enemy to another, or a nearby piece changed colour and\nturned a friend into an enemy. Contact is always the residue of something that\nalready happened.</p>\n<p>So a cluster of touching pieces is not a battle about to start. It is a battle\nthat already finished, and the pieces in it are, for the moment, spent. The next\nstrike will come from somewhere else, from the quiet part of the board where a\npiece is sitting two steps from a door nobody is watching.</p>"
   }
  ]
 },
 {
  "slug": "13-cramp",
  "title": "Cramp",
  "blocks": [
   {
    "type": "md",
    "html": "<h1>Cramp</h1>\n<p>You can lose a game of Tarati without ever being behind. You lose it by running\nout of moves, and a player who has never lost that way does not yet believe how\noften it happens.</p>\n<p>This chapter is about the resource that runs out. It is also, honestly, a chapter\nabout a measurement that failed first, because the failure teaches the idea\nbetter than the success does.</p>\n<h2>The measurement that failed</h2>\n<p>Cobs move one way. A white cob works its way toward Black's edge and can never\nturn around. So each side starts the game owning a fixed number of forward steps\n(thirty-eight, if you count them) and spends one every time a cob moves. It\nlooked like the cleanest idea in the book: count each side's remaining steps,\ncall the difference the <strong>road</strong>, and see who wins.</p>\n<p>It works beautifully. The side with twelve more steps of road scores 87%. The side\nwith twelve fewer scores 14%. A textbook curve, across twenty-three thousand\npositions.</p>\n<p>It is also almost entirely wrong, and here is why. A side with five pieces has\nmore cobs than a side with three, and more cobs means more road. Road was\nmeasuring the piece count wearing a disguise. Hold the material level, comparing\nonly positions where both sides have exactly four pieces, and the curve\ncollapses to a flat line: 50.7%, 48.6%, 50.9%, 49.9%, 44.9% across the whole\nrange. Nothing.</p>\n<p><strong>Total road is not a thing.</strong> If you take one habit from this chapter, take this\none: before you believe a positional rule, check it between players who are level\non material. Most rules that sound wise are the piece count in a hat.</p>\n<h2>What is actually true</h2>\n<p>The idea was not wrong, it was too smooth. Road does not matter as a pool you\ndraw down. It matters at the moment a particular cob has nowhere left to go.</p>\n<p>A cob outside its own home base can only move forward. Fill every forward square\nin front of it and it is <strong>stuck</strong>. Not slow, not awkward, but out of the game\nuntil the board opens or it promotes. Count those, and hold material level:</p>\n<table>\n<thead>\n<tr>\n<th style=\"text-align: center;\">Stuck cobs, you minus them</th>\n<th style=\"text-align: center;\">Your score</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td style=\"text-align: center;\">\u22121 (they have one more)</td>\n<td style=\"text-align: center;\">60.7%</td>\n</tr>\n<tr>\n<td style=\"text-align: center;\">0</td>\n<td style=\"text-align: center;\">49.6%</td>\n</tr>\n<tr>\n<td style=\"text-align: center;\">+1</td>\n<td style=\"text-align: center;\">42.2%</td>\n</tr>\n<tr>\n<td style=\"text-align: center;\">+2</td>\n<td style=\"text-align: center;\"><strong>20.3%</strong></td>\n</tr>\n</tbody>\n</table>\n<p>Each stuck cob is worth roughly ten points of result. Two of them and you are\nlosing four games in five, at dead level material. That is a bigger swing than\nmost single strikes, and there is nothing on the board to show for it: you count\nfour pieces, they count four pieces, and you are lost.</p>\n<p>The move count says the same thing from the other side. At equal material, three\nextra legal moves is worth about nine points, and the far ends of the range run\nfrom 34.5% to 70.9%.</p>\n<table>\n<thead>\n<tr>\n<th style=\"text-align: center;\">Legal moves, you minus them</th>\n<th style=\"text-align: center;\">Your score</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td style=\"text-align: center;\">\u22126 or worse</td>\n<td style=\"text-align: center;\">34.5%</td>\n</tr>\n<tr>\n<td style=\"text-align: center;\">\u22123</td>\n<td style=\"text-align: center;\">43.6%</td>\n</tr>\n<tr>\n<td style=\"text-align: center;\">level</td>\n<td style=\"text-align: center;\">53.0%</td>\n</tr>\n<tr>\n<td style=\"text-align: center;\">+3</td>\n<td style=\"text-align: center;\">59.4%</td>\n</tr>\n<tr>\n<td style=\"text-align: center;\">+6 or better</td>\n<td style=\"text-align: center;\">70.9%</td>\n</tr>\n</tbody>\n</table>\n<p>The board-control chapter already told you mobility was valuable, from a\nregression across ninety thousand positions. This is the same claim tested a\nharder way, with material held level so it cannot be the piece count in disguise,\nand it survives.</p>\n<h2>Playing it</h2>\n<p><strong>Count their stuck cobs, then count yours.</strong> It takes seconds and it is the most\ninformative number on the board that is not the piece count. A cob with a friend\ndirectly in front of it and nowhere to step is a piece you own on paper and do\nnot have.</p>\n<p><strong>Your own pieces cause most of your cramp.</strong> The forward squares in front of your\ncobs are usually blocked by <em>your other cobs</em>, not by the enemy. Advancing two\ncobs up the same file is the commonest self-inflicted wound in the game. Spread\nthe front.</p>\n<p><strong>Attack the front cob, not the back one.</strong> If you want to jam an opponent, the\npiece to block is the one furthest advanced, because everything behind it queues\nup. One well-placed piece in front of a leading cob can freeze two or three.</p>\n<p><strong>A rok is never stuck.</strong> Roks move in any direction, so they neither run out of\nroad nor block themselves. This is the real reason the endgame chapter says a rok\nwins endgames. It is not that a rok is worth more material, it is that it is the\nonly piece\nimmune to the thing that actually decides level endings.</p>\n<h2>Cramp and doors are the same trade</h2>\n<p>Put this chapter next to the one on doors and the tension is exact.</p>\n<p>A piece with no empty neighbours cannot be struck. A piece with no empty forward\nsquares cannot move. Those are the same empty squares. Safety and mobility are\ndrawn from one account, and every move you make spends from it in one direction\nor the other.</p>\n<p>That is Tarati's central bargain, and knowing it is most of what separates a\nstrong player from an average one. The average player asks whether a move is\nsafe. The strong player asks what it costs to be safe there, and whether the\nposition can afford it.</p>"
   }
  ]
 },
 {
  "slug": "14-power-moves",
  "title": "The moves you are not looking at",
  "blocks": [
   {
    "type": "md",
    "html": "<h1>The moves you are not looking at</h1>\n<p>Take a sharp position, one where a single move beats every alternative by more\nthan a whole piece, and ask what that move is. Three times in four it takes\nsomething, and you would have found it.</p>\n<p>The fourth time it takes nothing at all.</p>\n<p>Across 28,274 such positions, the winning move was <strong>not a strike 26.2% of the\ntime</strong> (interval 25.7% to 26.7%). Those are the moves that separate a decent club\nplayer from a strong one, and they are invisible to the search most players run,\nwhich is: what can I take, and what can they take. Nothing is being taken. The\nmove looks like a pass.</p>\n<h2>The four things a quiet move is doing</h2>\n<p>Every one of those moves is doing a job. There are four, and a single move often\ndoes two or three at once, which is usually why it is worth a piece when nothing\nelse is.</p>\n<table>\n<thead>\n<tr>\n<th>What it does</th>\n<th style=\"text-align: center;\">Share of the quiet winners</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td><strong>Builds a threat</strong>, creating a strike you did not have</td>\n<td style=\"text-align: center;\">39.3%</td>\n</tr>\n<tr>\n<td><strong>Shuts a door</strong>, taking an empty square out of play</td>\n<td style=\"text-align: center;\">21.5%</td>\n</tr>\n<tr>\n<td><strong>Escapes</strong>, moving a piece that was about to be taken</td>\n<td style=\"text-align: center;\">15.1%</td>\n</tr>\n<tr>\n<td><strong>Promotes</strong>, reaching the far home base to make a rok</td>\n<td style=\"text-align: center;\">11.8%</td>\n</tr>\n</tbody>\n</table>\n<p><strong>The threat-build.</strong> The commonest quiet power move, and the one the\nboard-control chapter already showed you: because you cannot take a piece you are\nalready touching, an attack has to be launched from just outside contact. The\nmove that sets it up captures nothing and looks idle. It is the move that wins.</p>\n<p><strong>The shut.</strong> The doors chapter, turned into a weapon. The opponent's\nstrike needs a specific empty square to arrive on; put a piece on that square, or\nforce one there, and the threat is gone permanently rather than for a move. A\nshut is the closest thing Tarati has to a defensive resource that does not\nretreat.</p>\n<p><strong>The escape.</strong> The one most players do find, and the reason this category is\nsmaller than you would guess. It is not that escapes are rare, it is that they\nare easy, so they rarely turn out to be the <em>only</em> move that wins by a piece.</p>\n<p><strong>The promotion.</strong> Small in share and large in consequence. See the endgame\nchapter for why.</p>\n<h2>How deep you have to look to find each one</h2>\n<p>This is the part worth memorising, because it tells you where your effort should\ngo. For each kind of winning move, we measured the shallowest search depth at\nwhich the engine picks it:</p>\n<table>\n<thead>\n<tr>\n<th>Kind of move</th>\n<th style=\"text-align: center;\">Median depth</th>\n<th style=\"text-align: center;\">Found within 2 ply</th>\n<th style=\"text-align: center;\">Needs 7+ ply</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td>Strike</td>\n<td style=\"text-align: center;\">1</td>\n<td style=\"text-align: center;\">91%</td>\n<td style=\"text-align: center;\">0%</td>\n</tr>\n<tr>\n<td>Promotion</td>\n<td style=\"text-align: center;\">1</td>\n<td style=\"text-align: center;\">76%</td>\n<td style=\"text-align: center;\">12%</td>\n</tr>\n<tr>\n<td>Escape</td>\n<td style=\"text-align: center;\">1</td>\n<td style=\"text-align: center;\">68%</td>\n<td style=\"text-align: center;\">7%</td>\n</tr>\n<tr>\n<td>Threat-build</td>\n<td style=\"text-align: center;\">3</td>\n<td style=\"text-align: center;\"><strong>42%</strong></td>\n<td style=\"text-align: center;\">20%</td>\n</tr>\n<tr>\n<td>Shut</td>\n<td style=\"text-align: center;\">3</td>\n<td style=\"text-align: center;\"><strong>46%</strong></td>\n<td style=\"text-align: center;\">17%</td>\n</tr>\n</tbody>\n</table>\n<p>Strikes are one-ply moves. Nine in ten are visible by looking a single move\nahead, which is what everyone does, which is why nobody gains anything by being\ngood at them past a certain point.</p>\n<p>Threat-builds and shuts sit at a median of three ply and fewer than half are\nvisible within two. One in five threat-builds needs seven ply or more, which at\nthe board means you are not going to calculate it. You will find it by\nrecognising the shape, or you will not find it.</p>\n<p>That is the whole argument for learning patterns rather than calculating harder.\nThe moves worth learning are precisely the ones calculation does not reach.</p>\n<h2>Two shapes to know by sight</h2>\n<p><strong>The step-back.</strong> Your piece is touching an enemy and neither can hurt the\nother. Step <em>away</em> to a square from which the enemy's square is two steps off,\nand the threat exists again next move. It looks like a retreat. It is the attack.\nThe board-control chapter shows the sequence; what it did not say is that this is\nthe single commonest quiet winner in the game.</p>\n<p><strong>The plug.</strong> One of your pieces is takeable and you cannot move it safely. Do\nnot move it. Find the empty square the strike has to come through and fill that\ninstead. The attacker is still there, still aimed, and now permanently unable to\narrive.</p>"
   },
   {
    "type": "figure",
    "caption": "Black to move. White threatens B1-B2, arriving beside C4 and taking it, and running C4 forward to C3 does not help, because B2 covers that too. Black plays A1-B2 instead. It takes nothing and it stands in the only doorway, and the threat is gone for good.",
    "board": {
     "checkers": {
      "A1": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "B1": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "B6": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C4": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C6": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C7": {
       "color": "BLACK",
       "isUpgraded": false
      },
      "C12": {
       "color": "WHITE",
       "isUpgraded": false
      },
      "D1": {
       "color": "WHITE",
       "isUpgraded": true
      }
     },
     "arrows": [
      [
       "A1",
       "B2"
      ]
     ],
     "highlightVertices": [
      "C4"
     ],
     "sub": null
    }
   },
   {
    "type": "md",
    "html": "<h2>When you are stuck, ask a different question</h2>\n<p>The practical version of this chapter is a change to the question you ask when\nnothing obvious presents itself.</p>\n<p>Most players, finding no capture, ask <em>what is the most active move?</em> and push a\ncob forward. Forward moves spend road, open doors, and are irreversible: three\ncosts, for a move chosen because nothing else came to mind.</p>\n<p>Ask instead, in this order:</p>\n<ol>\n<li><strong>What do they threaten next move?</strong> Not what is attacking now, what arrives.</li>\n<li><strong>Which empty square does that threat need?</strong> Fill it if you can.</li>\n<li><strong>What could I threaten, if one of my pieces were one square elsewhere?</strong> That\n   square is where the threat-build goes.</li>\n<li><strong>Only then</strong>, if all three come up empty, advance, and advance the cob whose\n   forward square is not in front of another of your cobs.</li>\n</ol>\n<p>One more number, for calibration. In the 22,349 positions where the best move\nsettles the result outright rather than winning material, the winning move is\nquiet <strong>21.6%</strong> of the time. So even in the most forcing positions on the board,\nmore than one game-deciding move in five takes nothing at all. If your search is\n\"what can I capture\", you will lose those games without ever seeing what hit\nyou.</p>"
   }
  ]
 },
 {
  "slug": "15-appendix",
  "title": "Appendix",
  "blocks": [
   {
    "type": "md",
    "html": "<h1>Appendix</h1>\n<h2>Opening reference</h2>\n<p>The two opening ideas and the four common ways the first two moves go, with the\npractical result from matched-strength games. At perfect play all are equal; the\npercentages are how they score in real play.</p>\n<table>\n<thead>\n<tr>\n<th>Line</th>\n<th>Idea</th>\n<th style=\"text-align: center;\">White</th>\n<th style=\"text-align: center;\">Black</th>\n<th style=\"text-align: center;\">Draw</th>\n<th>Character</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td>C1-B1 / C7-C6</td>\n<td>Inward vs ring</td>\n<td style=\"text-align: center;\"><strong>51%</strong></td>\n<td style=\"text-align: center;\">38%</td>\n<td style=\"text-align: center;\">11%</td>\n<td>White's best practical opening</td>\n</tr>\n<tr>\n<td>C1-C12 / C7-C6</td>\n<td>Ring vs ring</td>\n<td style=\"text-align: center;\">45%</td>\n<td style=\"text-align: center;\">42%</td>\n<td style=\"text-align: center;\">13%</td>\n<td>Quiet, solid, most drawish</td>\n</tr>\n<tr>\n<td>C1-C12 / C7-B4</td>\n<td>Ring vs inward</td>\n<td style=\"text-align: center;\">36%</td>\n<td style=\"text-align: center;\"><strong>54%</strong></td>\n<td style=\"text-align: center;\">10%</td>\n<td>Black's counter-thrust; respect it as White</td>\n</tr>\n<tr>\n<td>C1-B1 / C7-B4</td>\n<td>Inward vs inward</td>\n<td style=\"text-align: center;\">46%</td>\n<td style=\"text-align: center;\">47%</td>\n<td style=\"text-align: center;\">8%</td>\n<td>The fighting line, fewest draws</td>\n</tr>\n</tbody>\n</table>\n<p>The rule of thumb behind the table: the inward thrust toward the hub is the\naggressive choice for whoever plays it, but only pays if the piece is used to\nattack rather than left sitting in the exposed centre.</p>\n<h2>The value of things</h2>\n<p>What a depth-12 search pays for each feature of a position, in\ncentipieces, where one hundred is a full piece. These are the numbers behind the\nboard-control chapter, and several of them run against intuition.</p>\n<table>\n<thead>\n<tr>\n<th>Feature</th>\n<th style=\"text-align: center;\">Worth (centipieces)</th>\n<th>Note</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td>A piece</td>\n<td style=\"text-align: center;\">+100</td>\n<td>the unit</td>\n</tr>\n<tr>\n<td>A rok over a cob</td>\n<td style=\"text-align: center;\">+55</td>\n<td>freedom is worth about half a piece</td>\n</tr>\n<tr>\n<td>A move of mobility</td>\n<td style=\"text-align: center;\">+31</td>\n<td>far more than it looks</td>\n</tr>\n<tr>\n<td>Occupying the hub (A1)</td>\n<td style=\"text-align: center;\"><strong>\u221274</strong></td>\n<td>a liability, not an asset</td>\n</tr>\n<tr>\n<td>Occupying the inner ring (B)</td>\n<td style=\"text-align: center;\"><strong>\u221237</strong></td>\n<td>the whole centre is exposed</td>\n</tr>\n<tr>\n<td>A dead cob</td>\n<td style=\"text-align: center;\">\u221236</td>\n<td>worse than useless while still counted</td>\n</tr>\n</tbody>\n</table>\n<h2>The advanced numbers, on one page</h2>\n<p>Everything from the second half of the guide, in the form you would want at a\nboard. All of it is measured over positions from real games; the door table is\n144,378 pieces, the cramp tables hold material level, and the depth table is a\nsample of ninety positions per category.</p>\n<p><strong>Risk.</strong> Chance a piece can be taken by the opponent's very next move, by how\nmany <em>empty</em> squares touch it.</p>\n<table>\n<thead>\n<tr>\n<th>Doors</th>\n<th style=\"text-align: center;\">0</th>\n<th style=\"text-align: center;\">1</th>\n<th style=\"text-align: center;\">2</th>\n<th style=\"text-align: center;\">3</th>\n<th style=\"text-align: center;\">4</th>\n<th style=\"text-align: center;\">5</th>\n<th style=\"text-align: center;\">6</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td>Takeable now</td>\n<td style=\"text-align: center;\">0%</td>\n<td style=\"text-align: center;\">11%</td>\n<td style=\"text-align: center;\">19%</td>\n<td style=\"text-align: center;\">30%</td>\n<td style=\"text-align: center;\">41%</td>\n<td style=\"text-align: center;\">52%</td>\n<td style=\"text-align: center;\">82%</td>\n</tr>\n</tbody>\n</table>\n<p><strong>Cramp</strong>, at equal material. A stuck cob is one outside its own home base with\nno empty square in front of it.</p>\n<table>\n<thead>\n<tr>\n<th>Stuck cobs, you minus them</th>\n<th style=\"text-align: center;\">\u22121</th>\n<th style=\"text-align: center;\">0</th>\n<th style=\"text-align: center;\">+1</th>\n<th style=\"text-align: center;\">+2</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td>Your score</td>\n<td style=\"text-align: center;\">61%</td>\n<td style=\"text-align: center;\">50%</td>\n<td style=\"text-align: center;\">42%</td>\n<td style=\"text-align: center;\">20%</td>\n</tr>\n</tbody>\n</table>\n<table>\n<thead>\n<tr>\n<th>Legal moves, you minus them</th>\n<th style=\"text-align: center;\">\u22126</th>\n<th style=\"text-align: center;\">\u22123</th>\n<th style=\"text-align: center;\">0</th>\n<th style=\"text-align: center;\">+3</th>\n<th style=\"text-align: center;\">+6</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td>Your score</td>\n<td style=\"text-align: center;\">35%</td>\n<td style=\"text-align: center;\">44%</td>\n<td style=\"text-align: center;\">53%</td>\n<td style=\"text-align: center;\">59%</td>\n<td style=\"text-align: center;\">71%</td>\n</tr>\n</tbody>\n</table>\n<p><strong>Strikes.</strong> One strike is a two-piece swing, because the board always holds\neight and a struck piece changes sides rather than leaving.</p>\n<table>\n<thead>\n<tr>\n<th>Pieces flipped</th>\n<th style=\"text-align: center;\">one</th>\n<th style=\"text-align: center;\">two</th>\n<th style=\"text-align: center;\">three</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td>Share of strikes</td>\n<td style=\"text-align: center;\">82.7%</td>\n<td style=\"text-align: center;\">16.8%</td>\n<td style=\"text-align: center;\">0.5%</td>\n</tr>\n<tr>\n<td>Swing</td>\n<td style=\"text-align: center;\">2</td>\n<td style=\"text-align: center;\">4</td>\n<td style=\"text-align: center;\">6</td>\n</tr>\n</tbody>\n</table>\n<p>Twenty per cent of all legal moves strike something; thirty-eight per cent of\nmoves actually played do.</p>\n<p><strong>What wins, and how hard it is to see.</strong> In positions where one move beats the\nrest by more than a piece, 26.2% of the winners take nothing at all.</p>\n<table>\n<thead>\n<tr>\n<th>Kind of winning move</th>\n<th style=\"text-align: center;\">Share of the quiet ones</th>\n<th style=\"text-align: center;\">Found within 2 ply</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td>Builds a threat</td>\n<td style=\"text-align: center;\">39%</td>\n<td style=\"text-align: center;\">42%</td>\n</tr>\n<tr>\n<td>Shuts a door</td>\n<td style=\"text-align: center;\">22%</td>\n<td style=\"text-align: center;\">46%</td>\n</tr>\n<tr>\n<td>Escapes</td>\n<td style=\"text-align: center;\">15%</td>\n<td style=\"text-align: center;\">68%</td>\n</tr>\n<tr>\n<td>Promotes</td>\n<td style=\"text-align: center;\">12%</td>\n<td style=\"text-align: center;\">76%</td>\n</tr>\n<tr>\n<td><em>(a strike, for comparison)</em></td>\n<td style=\"text-align: center;\">n/a</td>\n<td style=\"text-align: center;\">91%</td>\n</tr>\n</tbody>\n</table>\n<p><strong>Two rules that hold without exception</strong>, checked against every strike and\nevery legal move in the corpus:</p>\n<ul>\n<li>A piece with no empty neighbour cannot be struck. <em>(0 exceptions in 3,425)</em></li>\n<li>No piece can strike an enemy it was already touching, and no move creates new\n  contact without striking. <em>(0 exceptions in 39,107 strikes and 10,450\n  contacts)</em></li>\n</ul>\n<h2>The difficulty ladder</h2>\n<p>The game's four difficulty settings are not labels for search depth. They were\nmeasured by a round robin of forty-eight thousand games and rated by Elo, so the\ngaps between them are real, tested differences in strength.</p>\n<table>\n<thead>\n<tr>\n<th>Setting</th>\n<th style=\"text-align: center;\">Approx. Elo</th>\n<th style=\"text-align: center;\">Best-move rate</th>\n</tr>\n</thead>\n<tbody>\n<tr>\n<td>Easy</td>\n<td style=\"text-align: center;\">0</td>\n<td style=\"text-align: center;\">45%</td>\n</tr>\n<tr>\n<td>Medium</td>\n<td style=\"text-align: center;\">450</td>\n<td style=\"text-align: center;\">57%</td>\n</tr>\n<tr>\n<td>Hard</td>\n<td style=\"text-align: center;\">740</td>\n<td style=\"text-align: center;\">68%</td>\n</tr>\n<tr>\n<td>Champion</td>\n<td style=\"text-align: center;\">1090</td>\n<td style=\"text-align: center;\">86%</td>\n</tr>\n</tbody>\n</table>\n<p>The \"best-move rate\" is how often each level plays the move a deep search\nprefers, measured over a hundred thousand real positions. It is a second,\nindependent confirmation that the ladder is genuine: accuracy rises with rating,\ncomputed a completely different way from the game results.</p>\n<h2>How this guide was made</h2>\n<p>Nothing here is opinion, and it is worth being precise about what that means and\nwhere it stops.</p>\n<p>The rules were implemented from the patent and checked against a suite of tests.\nAn engine was built on top of them and verified two ways: its evaluation is\nprovably symmetric between the two colours, and its search agrees move for move\nwith a separate implementation across hundreds of positions.</p>\n<p>The openings come from an exhaustive solve. Every distinct position reachable in\nthe first ten moves, all three hundred and seventy thousand of them, was\nenumerated and evaluated, so opening claims are proofs about the position rather\nthan statistics from games.</p>\n<p>The strategic claims come from a corpus of more than a million games and a pass\nin which an engine scored every legal move in over a hundred thousand positions\ndrawn from them. That is what lets the guide say what a move costs rather than\nonly how often games containing it were won. Every board shown is drawn\nfrom a real game.</p>\n<p>One honest limit, and one correction.</p>\n<p>The limit: \"best move\" means best against a strong reference, not against perfect\nplay. Every move in this guide was scored twice, by two engines a measured two\nhundred Elo apart, and the second pass changed the chosen move in a fifth of\npositions. The advanced chapters lean where they can on things that are true of\nthe position rather than things an engine prefers, since a piece with no empty\nneighbour cannot be struck whatever any engine thinks, but the move-quality\nnumbers still carry that ceiling.</p>\n<p>On depth, though, the guide is on firm ground. Searching deeper than twelve plies\nchanges almost nothing here: on a controlled test the depth-12 verdict matched a\ndepth-16 verdict on 94% of chosen moves and on 99% of the strike-versus-quiet\nclassifications this guide actually uses, for an eighth of the work. A median\nTarati game is only twenty-nine plies long, so twelve is already most of the way\nto the end. Depth was never the limit. Playing exchanges out before judging a\nposition was.</p>\n<p>The correction: earlier drafts of this guide reported a tempo effect worth\nroughly two pieces, on the evidence that the engine scored whichever side had to\nmove about 230 centipieces worse from the symmetric opening. That was an\nartefact. The engine had no quiescence search, so it was stopping its analysis in\nthe middle of exchanges, and the resulting error alternated in sign with every\nextra ply of depth, which is the signature of the mistake rather than of a\nproperty of the game. Re-run with a search that plays out the strikes before it evaluates,\nthe same position scores zero at every depth from six to fourteen. <strong>The opening\nis balanced and there is no large tempo effect.</strong></p>\n<p>What survives is the practice that the mistaken finding produced: this guide\njudges moves by what they cost relative to the best move at the same position,\nnever by an engine's absolute score. That was the right method for the wrong\nreason, and it is still the right method.</p>\n<h2>A note on measuring</h2>\n<p>Two of the findings in these chapters started life as something else, and both\nfailures are more instructive than the results that replaced them.</p>\n<p>The \"centre is a trap\" rule was originally a claim about how many lines meet at a\nsquare. That is nearly right, and wrong in a specific place: the four home-corner\npoints have four connections but are safer than the three-connection points,\nbecause their neighbours are squares an opponent struggles to reach. Counting\n<em>open</em> neighbours instead of all of them fixes it, and gives a number you can\ncompute at the board.</p>\n<p>The cramp chapter was going to be about road, the total forward steps each side\nhas left. It produces a beautiful curve, from 14% to 87%, and it is almost\nentirely the piece count in disguise: a side with more pieces has more cobs and\ntherefore more road. Held at equal material the curve is flat. Every positional\nclaim in this guide is now checked that way, and the ones that did not survive\nare not in it.</p>"
   }
  ]
 }
];
